/**
 * Iter-157E P0(2) — Restore lineage banner.
 *
 * Read-only metadata strip rendered on Project Home for any project
 * that was created via Restore-to-Copy. Shows:
 *
 *   • the source project's name (captured at clone time so a later
 *     rename or deletion of the source doesn't break the banner),
 *   • the restore-point label the clone was rehydrated from,
 *   • when the clone was created (human-readable timestamp).
 *
 * The banner exists to answer the #1 pilot question: "Which project
 * am I in?". It carries no actions — copies are first-class projects
 * after creation. The user can navigate, edit, restore, archive,
 * delete this project independently of the source.
 */

import { GitBranch, Clock, FileText } from "lucide-react";

function formatHumanDate(iso) {
    if (!iso) return null;
    try {
        const d = new Date(iso);
        return d.toLocaleString(undefined, {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
}

export default function RestoreLineageBanner({ project, className = "" }) {
    if (!project?.cloned_from_project_id) return null;
    const sourceName = project.cloned_from_project_name || "Source project";
    const restorePointLabel =
        project.cloned_from_restore_point_label ||
        project.cloned_from_restore_point_id ||
        "Restore Point";
    const createdAt = formatHumanDate(project.cloned_at);
    return (
        <section
            data-testid="restore-lineage-banner"
            className={`rounded-xl border border-violet-500/30 bg-violet-500/[0.06] p-4 ${className}`}
        >
            <div className="flex items-start gap-3">
                <div className="h-9 w-9 rounded-md bg-violet-500/15 text-violet-300 flex items-center justify-center shrink-0">
                    <GitBranch size={15} />
                </div>
                <div className="min-w-0 flex-1">
                    <p
                        data-testid="restore-lineage-banner-title"
                        className="text-[10px] uppercase tracking-[0.22em] text-violet-300 font-semibold"
                    >
                        Restored copy
                    </p>
                    <p className="text-[13px] text-violet-50 mt-1 leading-relaxed">
                        This project is a working copy. The original is
                        untouched.
                    </p>
                    <dl className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2 text-[12px]">
                        <div
                            data-testid="restore-lineage-source"
                            className="rounded-md border border-violet-500/20 bg-card/40 px-3 py-2"
                        >
                            <dt className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-neutral-400">
                                <FileText size={11} />
                                Created from
                            </dt>
                            <dd
                                data-testid="restore-lineage-source-name"
                                className="mt-0.5 font-semibold text-white truncate"
                            >
                                {sourceName}
                            </dd>
                        </div>
                        <div
                            data-testid="restore-lineage-restore-point"
                            className="rounded-md border border-violet-500/20 bg-card/40 px-3 py-2"
                        >
                            <dt className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-neutral-400">
                                <GitBranch size={11} />
                                Restore Point
                            </dt>
                            <dd
                                data-testid="restore-lineage-restore-point-label"
                                className="mt-0.5 font-semibold text-white truncate"
                            >
                                {restorePointLabel}
                            </dd>
                        </div>
                        <div
                            data-testid="restore-lineage-created-at"
                            className="rounded-md border border-violet-500/20 bg-card/40 px-3 py-2"
                        >
                            <dt className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-neutral-400">
                                <Clock size={11} />
                                Created
                            </dt>
                            <dd
                                data-testid="restore-lineage-created-at-value"
                                className="mt-0.5 font-semibold text-white truncate"
                            >
                                {createdAt || "—"}
                            </dd>
                        </div>
                    </dl>
                </div>
            </div>
        </section>
    );
}
